import React, { useMemo, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControl, InputLabel, MenuItem, Select, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@material-ui/core';
import { formatDate } from './commercialFormatters';
import { toLocalDateInputValue } from './commercialDateTime';

const today = () => toLocalDateInputValue();
const defaultRequestId = () => `receipt-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const formatMinor = (minor, currency = 'EUR') => (Number(minor || 0) / 100).toLocaleString('en-IE', {
  style: 'currency', currency: currency || 'EUR', maximumFractionDigits: 2,
});

const responseError = async (response, fallback) => {
  const body = await response.json().catch(() => ({}));
  return new Error(body.message || `${fallback} (${response.status})`);
};

const methodLabel = method => ({
  TRANSFER: 'Bank transfer', BANK_TRANSFER: 'Bank transfer', CARD: 'Card',
  DIRECT_DEBIT: 'Direct debit', CASH: 'Cash', OTHER: 'Other',
}[method] || String(method || 'Not provided').replace(/_/g, ' ').toLowerCase().replace(/^./, letter => letter.toUpperCase()));

export default function CommercialPaymentDialog({
  commitment, headers, hostname, onClose, onSaved, requestIdFactory = defaultRequestId,
  businessDate,
}) {
  const [form, setForm] = useState({
    amount: '', receivedAt: businessDate || today(), method: 'TRANSFER', reference: '', notes: '',
  });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const requestId = useMemo(() => requestIdFactory(), [requestIdFactory]);
  const currency = commitment.currency || 'EUR';
  const outstandingMinor = Number(commitment.outstandingMinor == null
    ? Math.max(0, Number(commitment.totalAmountMinor || 0) - Number(commitment.paidMinor || 0))
    : commitment.outstandingMinor);
  const amountMinor = Math.round(Number(form.amount || 0) * 100);
  const amountError = amountMinor > outstandingMinor
    ? `Amount cannot exceed the outstanding balance of ${formatMinor(outstandingMinor, currency)}.`
    : amountMinor <= 0 && form.amount !== '' ? 'Amount must be greater than zero.' : null;
  const canPreview = !saving && !amountError && amountMinor > 0 && Boolean(form.receivedAt);

  const update = field => event => setForm(current => ({ ...current, [field]: event.target.value }));

  const requestPreview = async () => {
    setSaving(true); setError(null);
    try {
      const body = { ...form, amount: Number(form.amount), requestId };
      const response = await fetch(`${hostname}/commercial/commitments/${commitment.id}/payments/preview`, {
        method: 'POST', headers, body: JSON.stringify(body),
      });
      if (!response.ok) throw await responseError(response, 'Could not preview receipt');
      setPreview(await response.json());
    } catch (cause) {
      setPreview(null); setError(cause.message);
    } finally { setSaving(false); }
  };

  const confirmReceipt = async () => {
    setSaving(true); setError(null);
    try {
      const response = await fetch(`${hostname}/commercial/commitments/${commitment.id}/payments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          amount: Number(preview.normalized.amountMinor || 0) / 100,
          receivedAt: preview.normalized.receivedAt,
          method: preview.normalized.method,
          reference: preview.normalized.reference,
          notes: preview.normalized.notes,
          requestId: preview.normalized.requestId,
          expectedVersion: preview.expectedVersion,
        }),
      });
      if (!response.ok) throw await responseError(response, 'Could not record receipt');
      onSaved(await response.json());
    } catch (cause) { setError(cause.message); } finally { setSaving(false); }
  };

  return <Dialog open fullWidth maxWidth="md" onClose={() => !saving && onClose()} aria-labelledby="receipt-dialog-title">
    <DialogTitle id="receipt-dialog-title">
      {preview ? 'Review receipt allocation' : `Record receipt · ${commitment.customerName}`}
    </DialogTitle>
    <DialogContent>
      {!preview ? <>
        <Typography variant="body2" color="textSecondary">
          Record cash received against this agreement. The server allocates it to the oldest outstanding payment stage. This does not change MRR.
        </Typography>
        <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gridGap={16} pt={2}>
          <Box gridColumn="1 / -1" p={1.5} bgcolor="grey.100">
            <Typography variant="overline" color="textSecondary">Available balance</Typography>
            <Typography variant="h6">{formatMinor(outstandingMinor, currency)} outstanding</Typography>
          </Box>
          <TextField
            required type="number" variant="outlined" label="Amount received"
            inputProps={{ min: .01, max: outstandingMinor / 100, step: .01, 'aria-label': 'Amount received' }}
            value={form.amount} onChange={update('amount')} error={Boolean(amountError)}
            helperText={amountError || `Maximum ${formatMinor(outstandingMinor, currency)}`}
          />
          <TextField required type="date" inputProps={{ 'aria-label': 'Received date' }} InputLabelProps={{ shrink: true }} variant="outlined" label="Received date" value={form.receivedAt} onChange={update('receivedAt')} />
          <FormControl variant="outlined">
            <InputLabel id="receipt-method-label">Payment method</InputLabel>
            <Select labelId="receipt-method-label" label="Payment method" value={form.method} onChange={update('method')}>
              <MenuItem value="TRANSFER">Bank transfer</MenuItem>
              <MenuItem value="CARD">Card</MenuItem>
              <MenuItem value="DIRECT_DEBIT">Direct debit</MenuItem>
              <MenuItem value="CASH">Cash</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField inputProps={{ 'aria-label': 'Payment reference' }} variant="outlined" label="Payment reference" value={form.reference} onChange={update('reference')} helperText="For example, bank or invoice reference" />
          <TextField inputProps={{ 'aria-label': 'Internal notes' }} style={{ gridColumn: '1 / -1' }} multiline minRows={2} variant="outlined" label="Internal notes" value={form.notes} onChange={update('notes')} />
        </Box>
      </> : <>
        <Typography variant="body2" color="textSecondary" paragraph>
          Check the server allocation below. It uses the oldest outstanding stage first. Confirming creates one immutable receipt; no MRR changes are made.
        </Typography>
        <Box component="section" aria-label="Receipt details" display="grid" gridTemplateColumns="repeat(3, minmax(0, 1fr))" gridGap={16} mb={2}>
          <div><Typography variant="caption" color="textSecondary">Customer</Typography><Typography>{commitment.customerName}</Typography></div>
          <div><Typography variant="caption" color="textSecondary">Amount</Typography><Typography>{formatMinor(preview.normalized.amountMinor, preview.currency)}</Typography></div>
          <div><Typography variant="caption" color="textSecondary">Received</Typography><Typography>{formatDate(preview.normalized.receivedAt)}</Typography></div>
          <div><Typography variant="caption" color="textSecondary">Method</Typography><Typography>{methodLabel(preview.normalized.method)}</Typography></div>
          <div><Typography variant="caption" color="textSecondary">Reference</Typography><Typography>{preview.normalized.reference || 'Not provided'}</Typography></div>
          <div><Typography variant="caption" color="textSecondary">After receipt</Typography><Typography>{formatMinor(preview.outstandingAfterMinor, preview.currency)} outstanding</Typography></div>
          <Box gridColumn="1 / -1"><Typography variant="caption" color="textSecondary">Internal notes</Typography><Typography>{preview.normalized.notes || 'Not provided'}</Typography></Box>
        </Box>
        <Divider />
        {preview.allocations.some(allocation => allocation.createsSchedule) && <Box mt={2}><Typography variant="body2" color="textSecondary">No payment schedule exists yet. Confirming creates this payment stage automatically before applying the receipt.</Typography></Box>}
        <Table size="small" aria-label="Receipt allocation">
          <TableHead><TableRow><TableCell>Payment stage</TableCell><TableCell>Due date</TableCell><TableCell align="right">Before</TableCell><TableCell align="right">Allocated</TableCell><TableCell align="right">Remaining</TableCell></TableRow></TableHead>
          <TableBody>{preview.allocations.map((allocation, index) => <TableRow key={allocation.installmentId || `automatic-stage-${allocation.dueDate || index}`}>
            <TableCell>{allocation.installmentId || (allocation.createsSchedule ? 'Automatic full-agreement payment stage' : 'Automatic payment stage')}</TableCell>
            <TableCell>{formatDate(allocation.dueDate)}</TableCell>
            <TableCell align="right">{formatMinor(allocation.beforePaidMinor, preview.currency)} paid</TableCell>
            <TableCell align="right">{formatMinor(allocation.amountMinor, preview.currency)}</TableCell>
            <TableCell align="right">{formatMinor(allocation.remainingMinor, preview.currency)}</TableCell>
          </TableRow>)}</TableBody>
        </Table>
      </>}
      {error && <Box mt={2}><Typography color="error" role="alert">{error}</Typography></Box>}
    </DialogContent>
    <DialogActions>
      {preview ? <>
        <Button disabled={saving} onClick={() => { setPreview(null); setError(null); }}>Back to details</Button>
        <Button disabled={saving} color="primary" variant="contained" onClick={confirmReceipt}>{saving ? 'Recording…' : 'Confirm and record receipt'}</Button>
      </> : <>
        <Button disabled={saving} onClick={onClose}>Cancel</Button>
        <Button disabled={!canPreview} color="primary" variant="contained" onClick={requestPreview}>{saving ? 'Preparing…' : 'Review receipt'}</Button>
      </>}
    </DialogActions>
  </Dialog>;
}
